import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import api from '../services/api';

const AssessmentTimer = ({ 
  assessmentId, 
  submissionId,
  initialTimeRemaining, 
  startedAt,
  duration,
  onTimeExpired
}) => {
  const [timeRemaining, setTimeRemaining] = useState(initialTimeRemaining || null);
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [loading, setLoading] = useState(false); // Start as not loading since we have initial time
  const intervalRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const lastSyncRef = useRef(null);

  // Format time as HH:MM:SS or MM:SS
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate remaining time based on server startTime and local clock
  const calculateTimeRemaining = () => {
    if (!startedAt || !duration) return initialTimeRemaining || 0;
    
    const now = Date.now();
    const start = new Date(startedAt).getTime();
    const elapsed = Math.floor((now - start) / 1000); // seconds elapsed
    const totalTime = duration * 60; // total time in seconds
    const remaining = Math.max(0, totalTime - elapsed);
    
    return remaining;
  };

  // Sync time with server for verification
  const syncWithServer = async () => {
    if (!submissionId || !assessmentId) return;

    try {
      const response = await api.get(`/api/assessments/${assessmentId}/time/${submissionId}`);
      const { timeRemaining: serverTime, expired } = response.data;
      
      lastSyncRef.current = Date.now();

      if (expired) {
        setTimeRemaining(0);
        if (onTimeExpired) {
          onTimeExpired();
        }
        clearInterval(intervalRef.current);
        clearInterval(syncIntervalRef.current);
      } else {
        // Update with server time to stay in sync
        setTimeRemaining(serverTime);
      }
    } catch (error) {
      console.error('Failed to sync timer:', error);
      // Continue with local calculation if sync fails
    }
  };

  // Initialize timer with initial value and start countdown
  useEffect(() => {
    if (!assessmentId || !submissionId) return;

    // Set initial time if available
    if (initialTimeRemaining !== null && initialTimeRemaining !== undefined) {
      setTimeRemaining(initialTimeRemaining);
    }

    // Sync with server immediately to verify
    syncWithServer();

    // Start countdown (runs every second)
    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev === undefined) {
          // If we don't have a value yet, try to calculate it
          return calculateTimeRemaining();
        }
        if (prev <= 1) {
          if (onTimeExpired) {
            onTimeExpired();
          }
          clearInterval(intervalRef.current);
          clearInterval(syncIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Sync with server every 10 seconds to stay accurate
    syncIntervalRef.current = setInterval(() => {
      syncWithServer();
    }, 10000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(syncIntervalRef.current);
    };
  }, [assessmentId, submissionId, initialTimeRemaining]);

  // Update warning states based on time remaining
  useEffect(() => {
    // Don't update warning states if time is not yet loaded
    if (timeRemaining === null) {
      setIsWarning(false);
      setIsCritical(false);
      return;
    }
    
    const minutes = Math.floor(timeRemaining / 60);
    
    if (minutes <= 1) {
      setIsCritical(true);
      setIsWarning(false);
    } else if (minutes <= 5) {
      setIsWarning(true);
      setIsCritical(false);
    } else {
      setIsWarning(false);
      setIsCritical(false);
    }
  }, [timeRemaining]);

  return (
    <div
      className={`fixed top-20 right-4 z-40 px-6 py-4 rounded-lg shadow-lg backdrop-blur-sm transition-all duration-300 ${
        loading 
          ? 'bg-slate-100/90 text-slate-900 border border-slate-300'
          : isCritical
          ? 'bg-red-500/90 text-white animate-pulse'
          : isWarning
          ? 'bg-yellow-500/90 text-slate-900'
          : 'bg-white/90 text-slate-900 border border-slate-200'
      }`}
    >
      <div className="flex items-center gap-3">
        {loading ? (
          <Clock className="w-6 h-6 animate-spin" />
        ) : isCritical ? (
          <AlertCircle className="w-6 h-6" />
        ) : (
          <Clock className="w-6 h-6" />
        )}
        <div>
          <div className="text-xs opacity-75 font-medium uppercase tracking-wide">
            {loading ? 'Loading Timer...' : isCritical ? 'Time Critical!' : isWarning ? 'Time Running Low' : 'Time Remaining'}
          </div>
          <div className="text-2xl font-bold font-mono tracking-wider">
            {loading ? '--:--' : formatTime(timeRemaining)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentTimer;

